<template>
  <v-dialog persistent scrollable width="800" v-model="dialog" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title>Registro da sessão</v-card-title>
      <v-card-text>
        <v-row class="mt-1">
          <v-col class="align-center">
            <v-icon class="mr-4">mdi-account</v-icon>
            <h3 class="d-inline">{{ customerName }}</h3>
          </v-col>
        </v-row>
        <v-row class="mt-1">
          <v-col class="align-center">
            <v-chip-group mandatory v-model="session.status">
              <v-chip small filter :value="'pending'">Pendente</v-chip>
              <v-chip small filter :value="'missed'" active-class="secondary">
                Faltou
              </v-chip>
              <v-chip small filter :value="'shown'" active-class="primary">
                Compareceu
              </v-chip>
            </v-chip-group>
          </v-col>
          <div class="mt-5 mb-4" v-if="canEditSessionAnnotations">
            <v-btn class="mr-2" v-if="canUserIa" @click="handleTextGenerationForm" small outlined color="primary">
              <app-icon left class="mr-2">wand_stars</app-icon>
              Editar com I.A
            </v-btn>
            <v-btn v-if="hasCustomer && transcription.status == null" @click="consentDialog = true" small outlined
              color="primary">
              <!-- <v-icon left>file_upload</v-icon> -->
              <app-icon class="mr-2">file_upload</app-icon>

              Transcrever
            </v-btn>

            <template v-if="transcription.status">
              <v-btn small outlined :color="transcriptionStatus.color" style="pointer-events: none;">
                <v-progress-circular v-if="transcription.loading" indeterminate size="14" width="2" class="mr-2"
                  :color="transcriptionStatus.color" />
                <v-icon v-else left small :color="transcriptionStatus.color">{{ transcriptionStatus.icon }}</v-icon>
                <span style="text-transform: none;">{{ transcriptionStatus.label }}</span>
              </v-btn>
              <v-btn v-if="canRetryTranscription" small text color="primary" class="ml-1" @click="retryTranscription">
                <v-icon left small>mdi-reload</v-icon>
                Tentar novamente
              </v-btn>
            </template>

          </div>
        </v-row>
        <v-row class="mt-1" v-if="canEditSessionAnnotations">
          <v-col cols="12" class="align-center">
            <v-textarea v-model="session.annotations" solo flat placeholder="Faça aqui suas anotações..."
              background-color="grey lighten-4" no-resize hide-details rows="15" />
            <!-- Resultado da transcrição -->
            <v-card v-if="transcription.status === 'done' && transcription.summary" outlined class="mt-4">
              <v-card-title class="subtitle-1 primary--text py-3">
                <v-icon left color="primary">mdi-microphone</v-icon>
                Resultado da Transcrição
              </v-card-title>
              <v-divider />
              <v-card-text>

                <!-- Resumo -->
                <div v-if="transcription.summary">
                  <div class="caption font-weight-bold secondary--text text-uppercase mb-2">Resumo da sessão</div>

                  <div v-if="transcription.summary.principais_temas && transcription.summary.principais_temas.length"
                    class="mb-2">
                    <div class="caption secondary--text mb-1">Principais temas</div>
                    <v-chip-group column>
                      <v-chip small v-for="(tema, i) in transcription.summary.principais_temas" :key="i">{{ tema
                      }}</v-chip>
                    </v-chip-group>
                  </div>

                  <div v-if="transcription.summary.progresso_observado" class="mb-2">
                    <div class="caption secondary--text mb-1">Progresso observado</div>
                    <p class="body-2 mb-0">{{ transcription.summary.progresso_observado }}</p>
                  </div>

                  <div v-if="transcription.summary.pontos_de_atencao && transcription.summary.pontos_de_atencao.length"
                    class="mb-2">
                    <div class="caption secondary--text mb-1">Pontos de atenção</div>
                    <ul class="pl-4 mb-0">
                      <li v-for="(ponto, i) in transcription.summary.pontos_de_atencao" :key="i" class="body-2">{{ ponto
                      }}</li>
                    </ul>
                  </div>

                  <v-btn small outlined color="primary" class="mt-2" @click="appendSummaryToAnnotations">
                    <v-icon left small>mdi-plus</v-icon> <span class="text-none"> Inserir resumo nas anotações
                    </span>
                  </v-btn>
                </div>

                <!-- Análise emocional -->
                <template v-if="transcription.sentiment_analysis">
                  <v-divider class="my-3" />
                  <div class="caption font-weight-bold secondary--text text-uppercase mb-2">Análise emocional</div>

                  <v-row dense>
                    <v-col cols="12" v-if="transcription.sentiment_analysis.estado_emocional_geral">
                      <div class="caption secondary--text mb-1">Estado emocional geral</div>
                      <p class="body-2 mb-0">{{ transcription.sentiment_analysis.estado_emocional_geral }}</p>
                    </v-col>
                    <v-col cols="12" md="6" v-if="transcription.sentiment_analysis.nivel_engajamento">
                      <div class="caption secondary--text mb-1">Nível de engajamento</div>
                      <p class="body-2 mb-0 text-capitalize">{{ transcription.sentiment_analysis.nivel_engajamento }}
                      </p>
                    </v-col>
                    <v-col cols="12"
                      v-if="transcription.sentiment_analysis.emocoes_identificadas && transcription.sentiment_analysis.emocoes_identificadas.length">
                      <div class="caption secondary--text mb-1">Emoções identificadas</div>
                      <v-chip-group column>
                        <v-chip small v-for="(e, i) in transcription.sentiment_analysis.emocoes_identificadas" :key="i"
                          class="text-capitalize">{{ e }}</v-chip>
                      </v-chip-group>
                    </v-col>
                    <v-col cols="12"
                      v-if="transcription.sentiment_analysis.momentos_criticos && transcription.sentiment_analysis.momentos_criticos.length">
                      <div class="caption secondary--text mb-1">Momentos críticos</div>
                      <ul class="pl-4 mb-0">
                        <li v-for="(m, i) in transcription.sentiment_analysis.momentos_criticos" :key="i"
                          class="body-2">{{ m }}</li>
                      </ul>
                    </v-col>
                    <v-col cols="12" v-if="transcription.sentiment_analysis.observacoes_clinicas">
                      <div class="caption secondary--text mb-1">Observações clínicas</div>
                      <p class="body-2 mb-0">{{ transcription.sentiment_analysis.observacoes_clinicas }}</p>
                    </v-col>
                  </v-row>
                  <v-btn small outlined color="primary" class="mt-2" @click="appendSentimentToAnnotations">
                    <v-icon left small>mdi-plus</v-icon>
                    <span class="text-none">Inserir análise emocional nas anotações</span>
                  </v-btn>
                </template>

                <!-- Prontuário -->
                <template v-if="transcription.medical_record">
                  <v-divider class="my-3" />
                  <div class="caption font-weight-bold secondary--text text-uppercase mb-2">Prontuário sugerido</div>

                  <v-row dense>
                    <v-col cols="12" v-if="transcription.medical_record.queixa_principal">
                      <div class="caption secondary--text mb-1">Queixa principal</div>
                      <p class="body-2 mb-0">{{ transcription.medical_record.queixa_principal }}</p>
                    </v-col>
                    <v-col cols="12" v-if="transcription.medical_record.conteudo_sessao">
                      <div class="caption secondary--text mb-1">Conteúdo da sessão</div>
                      <p class="body-2 mb-0">{{ transcription.medical_record.conteudo_sessao }}</p>
                    </v-col>
                    <v-col cols="12"
                      v-if="transcription.medical_record.intervencoes_realizadas && transcription.medical_record.intervencoes_realizadas.length">
                      <div class="caption secondary--text mb-1">Intervenções realizadas</div>
                      <v-chip-group column>
                        <v-chip small v-for="(int, i) in transcription.medical_record.intervencoes_realizadas"
                          :key="i">{{ int }}</v-chip>
                      </v-chip-group>
                    </v-col>
                    <v-col cols="12" v-if="transcription.medical_record.reacoes_do_paciente">
                      <div class="caption secondary--text mb-1">Reações do paciente</div>
                      <p class="body-2 mb-0">{{ transcription.medical_record.reacoes_do_paciente }}</p>
                    </v-col>
                    <v-col cols="12" v-if="transcription.medical_record.plano_proxima_sessao">
                      <div class="caption secondary--text mb-1">Plano para próxima sessão</div>
                      <p class="body-2 mb-0">{{ transcription.medical_record.plano_proxima_sessao }}</p>
                    </v-col>
                    <v-col cols="12" v-if="transcription.medical_record.observacoes_adicionais">
                      <div class="caption secondary--text mb-1">Observações adicionais</div>
                      <p class="body-2 mb-0">{{ transcription.medical_record.observacoes_adicionais }}</p>
                    </v-col>
                  </v-row>

                  <v-btn small outlined color="primary" class="mt-2" @click="appendMedicalRecordToAnnotations">
                    <v-icon left small>mdi-plus</v-icon>

                    <span class="text-none"> Inserir prontuário nas anotações</span>
                  </v-btn>
                </template>

              </v-card-text>
            </v-card>

            <template v-if="hasCustomer">
              <v-card outlined class="mt-4">
                <v-card-title class="subtitle-1 py-3 d-flex align-center">
                  <v-icon left>mdi-paperclip</v-icon>
                  Arquivos anexados
                  <v-spacer />
                  <v-btn small outlined color="primary" @click="handleFileForm">
                    <v-icon left small>mdi-plus</v-icon>
                    Anexar
                  </v-btn>
                </v-card-title>

                <template v-if="hasFiles">
                  <v-divider />
                  <app-pagination :showCount="false" @click="selectFiles($event)" :data="files">
                    <v-list dense>
                      <template v-for="(file, index) in files.data">
                        <v-list-item :key="index">
                          <v-list-item-icon class="mr-3 my-auto">
                            <v-icon color="grey darken-1">mdi-file-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>{{ file.name }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ $format.bytesToMB(file.size) }} · {{ $format.dateBr(file.created_at) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <div class="d-flex align-center flex-shrink-0 ml-2">
                            <v-btn @click.stop="handleDownloadFile(file)" icon small color="primary">
                              <v-icon small>mdi-download</v-icon>
                            </v-btn>
                            <app-delete-btn @click="handleDeleteFile(file.id)" type="icon" />
                          </div>
                        </v-list-item>
                        <v-divider :key="'d' + file.id" v-if="index" />
                      </template>
                    </v-list>
                  </app-pagination>
                </template>

                <v-card-text v-else class="text-center py-6 grey--text">
                  <v-icon color="grey lighten-1" large>mdi-paperclip</v-icon>
                  <div class="mt-2 caption">Nenhum arquivo anexado</div>
                </v-card-text>
              </v-card>
            </template>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col>
            <span class="info--text">
              <app-icon class="info--text">info</app-icon>
              Somente o profissional da sessão pode criar anotações da sessão.
            </span>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions id="form-card-footer" class="mt-0 pa-4">
        <app-back-btn @click="handleClose()" />
        <v-spacer v-if="$vuetify.breakpoint.xsOnly" />
        <app-confirm-btn @click="update()" />
      </v-card-actions>
    </v-card>
    <template v-if="hasCustomer">
      <FileUploadForm @store="selectFiles()" ref="FileUploadForm" title="Anexar Arquivos à Sessão" :params="fileParams"
        :url="url" />
    </template>
    <AppConfirmDiscardChanges ref="appConfirmDiscardChanges" />
    <TextGenerationForm ref="TextGenerationForm" @store="setText" />

    <!-- Modal de consentimento para transcrição -->
    <v-dialog v-model="consentDialog" max-width="500" persistent>
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text :class="['d-flex', $vuetify.breakpoint.xsOnly ? 'flex-column align-center text-center' : '']">
          <div :class="$vuetify.breakpoint.xsOnly ? 'mb-4' : 'pr-6'">
            <v-avatar color="#e8f0fe" :size="$vuetify.breakpoint.xsOnly ? 56 : 40">
              <v-icon color="primary" :large="$vuetify.breakpoint.xsOnly">mdi-shield-check-outline</v-icon>
            </v-avatar>
          </div>
          <div>
            <h3 class="mb-3">Consentimento para Transcrição</h3>
            <span class="body-2">
              <strong>Confirmo o consentimento da paciente.</strong>
              O arquivo de áudio será excluído permanentemente após a transcrição, em conformidade com a LGPD.
              Apenas o texto e a análise serão armazenados.
            </span>
            <div class="mt-3 caption grey--text">
              <v-icon x-small class="mr-1 grey--text">mdi-information-outline</v-icon>
              A IA poderá apresentar erros de interpretação. Este material gerado deve ser sempre revisado pelo
              profissional.
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <app-back-btn @click="consentDialog = false">Voltar</app-back-btn>
          <v-spacer />
          <app-confirm-btn @click="acceptConsentAndUpload">Aceitar</app-confirm-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <input class="d-none" ref="transcriptionInput" id="transcriptionInput" type="file"
      accept=".3gp,.3gpp,.aac,.flac,.m4a,.mp3,.ogg,.opus,.wav,.webm,.wma" @input="sendTranscription" />
  </v-dialog>
</template>

<script>
import FileUploadForm from "@/components/app/forms/FileUploadForm";
import AppConfirmDiscardChanges from "@/components/app/sections/AppConfirmDiscardChanges";
import TextGenerationForm from "@/components/assistant/forms/TextGenerationForm.vue";

export default {
  components: { FileUploadForm, AppConfirmDiscardChanges, TextGenerationForm },
  data() {
    return {
      url: "/customer/customer-files",
      session: {
        professional: {},
        status: "shown",
        customer: {},
        session_group: {},
        annotations: null,
      },

      files: {},
      dialog: false,


      // Transcrição
      consentDialog: false,
      pollingInterval: null,
      transcription: {
        status: null,
        job_id: null,
        medical_record: null,
        sentiment_analysis: null,
        summary: null,
        loading: false,
        error: null,
      },

    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    customerName() {
      return this.session.origin == "booking"
        ? this.session.booking.name
        : this.session.customer.name;
    },

    hasCustomer() {
      return this.session.customer_id != null;
    },

    canEditSessionAnnotations() {
      return this.user.id == this.session.professional.id;
    },

    fileParams() {
      return {
        session_id: this.session.id,
        customer_id: this.session.customer.id,
        professional_id: this.session.professional.id,
        collection: "session",
      };
    },

    hasFiles() {
      return this.files.data && this.files.data.length > 0;
    },

    hasText() {
      return this.session.annotations && this.session.annotations.length > 0;
    },

    canUserIa() {
      return this.canEditSessionAnnotations && this.$acl.isProfessional();
    },

    hasUnsavedChanges() {
      return this.$store.state.app.hasUnsavedChanges;
    },

    transcriptionStatus() {
      const labels = {
        uploading: "Enviando arquivo...",
        pending: "Procesando...",
        transcribing: "Transcrevendo...",
        analyzing: "Analisando com I.A...",
        done: "Transcrição concluída",
        error: "Erro no processamento",
      };
      const colors = {
        uploading: "grey",
        pending: "grey",
        transcribing: "orange",
        analyzing: "blue",
        done: "primary",
        error: "error",
      };
      const icons = {
        uploading: "mdi-upload",
        pending: "mdi-clock-outline",
        transcribing: "mdi-microphone",
        analyzing: "mdi-brain",
        done: "mdi-check-circle",
        error: "mdi-alert-circle",
      };
      const status = this.transcription.status;
      return {
        icon: icons[status] || "mdi-clock-outline",
        color: colors[status] || "grey",
        label: labels[status] || status,
      };
    },

    canRetryTranscription() {
      return this.transcription.status === "error" && this.hasCustomer;
    },
  },

  watch: {
    session: {
      handler(form) {
        this.$store.dispatch("app/hasChanges");
      },
      deep: true,
    },
  },

  methods: {
    async open(session) {

      this.stopPolling();
      this.consentDialog = false;
      this.transcription = {
        status: null,
        job_id: null,
        medical_record: null,
        sentiment_analysis: null,
        summary: null,
        loading: false,
        error: null,
      };

      this.session = JSON.parse(JSON.stringify(session));

      await this.selectFiles();

      this.$store.dispatch("app/resetChanges");

      this.dialog = true;
    },

    handleClose() {
      if (this.hasUnsavedChanges) {
        this.$refs.appConfirmDiscardChanges
          .askForSave()
          .then((res) => {
            this.update();
          })
          .catch((error) => {
            this.close();
          });
      } else {
        this.close();
      }
    },

    close() {
      this.stopPolling();
      this.$store.dispatch("app/resetChanges");

      this.dialog = false;
    },

    update() {
      this.$http
        .update("sessions/session-record", this.session.id, this.session)
        .then((response) => {
          console.log(response);
          this.processSaved();
        });
    },

    processSaved() {
      this.$emit("store", this.session.id);

      this.$store.dispatch("app/resetChanges");

      this.dialog = false;
    },

    handleFileForm() {
      this.$refs.FileUploadForm.open();
    },

    async selectFiles(page) {
      if (this.hasCustomer) {
        this.fileParams.page = page;
        this.$loading.start();
        await this.$http
          .index(this.url, this.fileParams)
          .then((response) => {
            this.files = response.files;
            this.$loading.finish();
          })
          .catch((error) => {
            this.$loading.finish();
          });
      }
    },

    handleDownloadFile(file) {
      this.$loading.start();
      this.$http
        .download(`${this.url}/download`, { id: file.id })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", file.name);
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          this.$loading.finish();
        });
    },

    handleDeleteFile(document_id) {
      this.$loading.start();
      this.$http
        .destroy(this.url, document_id)
        .then((response) => {
          this.selectFiles();
          this.$loading.finish();
        })
        .catch((error) => {
          this.selectFiles();
          this.$loading.finish();
        });
    },

    handleTextGenerationForm() {
      this.$refs.TextGenerationForm.open(this.session.annotations);
    },

    setText(text) {
      this.session.annotations = text;
    },


    // ---- Transcrição ----
    openTranscriptionPanel() {
      this.$refs.transcriptionInput.value = null;
      this.$nextTick(() => {
        this.$refs.transcriptionInput.click();
      });
    },

    acceptConsentAndUpload() {
      this.consentDialog = false;
      this.$nextTick(() => {
        this.openTranscriptionPanel();
      });
    },

    retryTranscription() {
      this.transcription.status = null;
      this.transcription.error = null;
      this.transcription.job_id = null;
      this.consentDialog = true;
    },

    async sendTranscription(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.transcription.loading = true;
      this.transcription.status = "uploading";
      this.transcription.error = null;

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await this.$http.store("sessions/transcription", formData);

        if (!response || !response.job_id) {
          throw new Error("Resposta inválida da API");
        }

        this.transcription.job_id = response.job_id;
        this.transcription.status = response.status || "pending";
        this.transcription.loading = false;

        this.startPolling();
      } catch (error) {
        this.transcription.status = "error";
        this.transcription.error = "Falha ao enviar o arquivo para transcrição.";
        this.transcription.loading = false;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        await this.checkStatus();
      }, 7000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      if (this.transcription.timer) {
        clearInterval(this.transcription.timer);
        this.transcription.timer = null;
      }
    },

    async checkStatus() {
      await this.$http.index(`sessions/transcription/status/${this.transcription.job_id}`)
        .then(async (response) => {

          this.transcription.status = response.status;

          if (response.status === "done") {
            this.stopPolling();
            await this.fetchResult();
          }

          if (response.status === "error") {
            this.stopPolling();
            this.transcription.error = response.error_message || "Erro no processamento da transcrição";
          }
        })
        .catch((error) => {
          this.transcription.status = "error";
          this.stopPolling();
        });
    },

    async fetchResult() {
      try {
        const response = await this.$http.index(`sessions/transcription/result/${this.transcription.job_id}`);
        this.transcription.summary = response.summary;
        this.transcription.sentiment_analysis = response.sentiment_analysis;
        this.transcription.medical_record = response.medical_record;
      } catch (error) {
        this.transcription.status = "error";
        this.transcription.error = "Falha ao buscar o resultado da transcrição.";
      } finally {
        this.transcription.loading = false;
      }
    },

    appendToAnnotations(text) {
      this.session.annotations = this.session.annotations
        ? `${this.session.annotations}\n\n${text}`
        : text;
    },

    appendSummaryToAnnotations() {
      const s = this.transcription.summary;
      let text = "=== Resumo da Sessão ===\n";
      if (s.principais_temas?.length) text += `Principais temas: ${s.principais_temas.join(", ")}\n`;
      if (s.progresso_observado) text += `Progresso: ${s.progresso_observado}\n`;
      if (s.pontos_de_atencao?.length) text += `Pontos de atenção: ${s.pontos_de_atencao.join("; ")}\n`;
      if (s.duracao_estimada) text += `Duração estimada: ${s.duracao_estimada}`;
      this.appendToAnnotations(text);
    },

    appendSentimentToAnnotations() {
      const s = this.transcription.sentiment_analysis;
      let text = "=== Análise Emocional ===\n";
      if (s.estado_emocional_geral) text += `Estado emocional: ${s.estado_emocional_geral}\n`;
      if (s.nivel_engajamento) text += `Nível de engajamento: ${s.nivel_engajamento}\n`;
      if (s.emocoes_identificadas?.length) text += `Emoções identificadas: ${s.emocoes_identificadas.join(", ")}\n`;
      if (s.momentos_criticos?.length) text += `Momentos críticos: ${s.momentos_criticos.join("; ")}\n`;
      if (s.observacoes_clinicas) text += `Observações clínicas: ${s.observacoes_clinicas}`;
      this.appendToAnnotations(text);
    },

    appendMedicalRecordToAnnotations() {
      const m = this.transcription.medical_record;
      let text = "=== Prontuário Sugerido ===\n";
      if (m.queixa_principal) text += `Queixa principal: ${m.queixa_principal}\n`;
      if (m.conteudo_sessao) text += `Conteúdo da sessão: ${m.conteudo_sessao}\n`;
      if (m.intervencoes_realizadas?.length) text += `Intervenções: ${m.intervencoes_realizadas.join(", ")}\n`;
      if (m.reacoes_do_paciente) text += `Reações do paciente: ${m.reacoes_do_paciente}\n`;
      if (m.plano_proxima_sessao) text += `Plano próxima sessão: ${m.plano_proxima_sessao}\n`;
      if (m.observacoes_adicionais) text += `Observações adicionais: ${m.observacoes_adicionais}`;
      this.appendToAnnotations(text);
    },
  },
};
</script>
